/**
 * The label renderers for everything that lives in `shared/`.
 *
 * `shared/` is imported by the Worker, the CSV exports and the OBS overlay, so
 * it stays English and it stays pure: a dictionary has no business inside it.
 * What it does instead is expose the STRUCTURE (`SlotDescriptor`, `GameLabel`,
 * the spirit and stat keys), and this module turns that structure into words.
 * The logic behind a label therefore has exactly one implementation and the two
 * formatters differ only in language, which is what stops the stream and the
 * board disagreeing about what a rewired bracket says.
 *
 * These are plain functions, not hooks. A component that renders one already
 * calls `useT()` somewhere up its tree (the page does), and React re-renders
 * the subtree when the locale store changes, so the words follow the switcher.
 */

import type { StatKey } from "../../shared/player-stats";
import type { SpiritCategory } from "../../shared/spirit";
import {
  type Division,
  effectiveLabel,
  type Field,
  type FieldId,
  type GameLabel,
  type LabelCtx,
  type Period,
  type Pool,
  type ScheduledGame,
  type Slot,
  type SlotDescriptor,
  slotDescriptor,
} from "../../shared/types";
import { ordinal } from "./format";
import { t } from "./t";

// ---------- period ----------

/** The narrow strip on the board: "1 ТАЙМ", "ЗАВЕРШЕНО". */
export function periodShort(period: Period): string {
  return t(`period.short.${period}`);
}

/** Plain words for the scorekeeper, which never uses jargon: "Другий тайм". */
export function periodLong(period: Period): string {
  return t(`period.long.${period}`);
}

// ---------- names with a fallback ----------

/**
 * A field's name. The name itself is user content and is never translated;
 * only the fallback for a field that has been deleted out from under a game is.
 */
export function fieldNameOf(fields: Field[], id: FieldId): string {
  return fields.find((f) => f.id === id)?.name ?? t("name.field", { id });
}

export function divisionNameOf(divisions: Division[], id: string | undefined): string {
  if (id === undefined) return t("name.noDivision");
  return divisions.find((d) => d.id === id)?.name ?? t("name.noDivision");
}

/**
 * The board's short tag for a division ("OPEN", "W"), or undefined when the
 * game carries none. Distinct from `divisionNameOf`, which always answers with
 * a sentence: a public row that says "No division" is noise, not information.
 *
 * ALSO undefined when the event HAS only one division (2026-09-19): a tag
 * tells two divisions apart, and with one there is nothing to tell apart -
 * it just prints the same word on the Live hero and again on every row under
 * it, four to six «OPEN»s on one phone screen.
 */
export function divisionTagOf(divisions: Division[], id: string | undefined): string | undefined {
  if (id === undefined || divisions.length < 2) return undefined;
  return divisions.find((d) => d.id === id)?.short;
}

export function poolNameOf(pools: Pool[], id: string | undefined): string {
  if (id === undefined) return t("name.noPool");
  return pools.find((p) => p.id === id)?.name ?? t("name.noPool");
}

// ---------- generated game notes ----------

/**
 * The words for a structural game label: "Півфінал 2", "Semifinal 2".
 *
 * `poolRound` interpolates the pool's NAME, which the organiser typed and which
 * is therefore passed straight through in both languages.
 */
export function gameLabelText(label: GameLabel): string {
  switch (label.kind) {
    case "final":
      return t("note.final");
    case "semifinal":
      return t("note.semifinal", { n: label.n });
    case "quarterfinal":
      return t("note.quarterfinal", { n: label.n });
    case "roundOf":
      return t("note.roundOf", { half: label.teams / 2, n: label.n });
    case "thirdPlace":
      return t("note.thirdPlace");
    case "grandFinal":
      return label.ifNecessary === true ? t("note.grandFinalIfNeeded") : t("note.grandFinal");
    case "losersFinal":
      return t("note.losersFinal");
    case "losersSemifinal":
      return t("note.losersSemifinal");
    case "losersRound":
      return label.i === undefined
        ? t("note.losersRound", { n: label.n })
        : t("note.losersRoundIndexed", { n: label.n, i: label.i });
    case "winnersBracket":
      return t("note.winnersBracket", { name: gameLabelText(label.inner) });
    case "poolRound":
      return t("note.poolRound", { pool: label.pool ?? t("note.pool"), n: label.n });
    case "placement":
      return t("note.placement", { place: ordinal(label.place) });
    case "placementRound":
      // The round's own name comes first in Ukrainian («Півфінал 1 за 5-те
      // місце») and last in English; the message literal decides, not this.
      return t("note.placementRound", {
        place: ordinal(label.place),
        name: gameLabelText(label.inner),
      });
    case "swissRound":
      return t("note.swissRound", { n: label.n });
  }
}

/**
 * What to show for a game's note.
 *
 * THE rule for keeping a locale out of stored data: translate only while the
 * note is still exactly what the generator wrote. The moment an organiser
 * retypes it, it stops matching and becomes user content: shown verbatim in
 * both languages, because "Показовий матч" is not something this app may
 * rewrite into English.
 */
export function gameNoteOf(game: Pick<ScheduledGame, "note" | "label">): string | undefined {
  // WHICH label may speak is decided in shared (`effectiveLabel`, beside the
  // parser and shared with the result card); this side only turns the answer
  // into words. A note with no label behind it stays exactly as written.
  const label = effectiveLabel(game.note, game.label);
  return label !== undefined ? gameLabelText(label) : game.note;
}

// ---------- slots ----------

/** "OPEN 2", «Переможець Півфінал 1»: a side that has not resolved yet. */
export function slotTextOf(slot: Slot | null, ctx: LabelCtx): string {
  return slotDescriptorText(slotDescriptor(slot, ctx));
}

/**
 * The same words, from a descriptor the SERVER built.
 *
 * The spirit payload (`/api/spirit/team/:team/games`) resolves its own
 * opponents server-side: doing it here would need the whole schedule plus live
 * standings, which is exactly the weight that endpoint exists to avoid, so the
 * Worker sends the structure and this renders it. The team hub's other views
 * read the schedule anyway and go through `slotTextOf` instead.
 */
export function slotDescriptorText(d: SlotDescriptor): string {
  switch (d.kind) {
    case "tbd":
      return t("slot.tbd");
    case "team":
      return d.name;
    case "seed":
      return `${d.group ?? t("slot.unknownDivision")} ${d.rank}`;
    case "outcome": {
      // The feeding game's own note may itself be a generated label, so it goes
      // through the same rule rather than being pasted in English.
      const game = d.label !== undefined || d.note !== undefined ? gameNoteOf(d) : undefined;
      const name = game ?? t("slot.game");
      return d.won ? t("slot.winner", { game: name }) : t("slot.loser", { game: name });
    }
  }
}

// ---------- spirit ----------

export function spiritCategoryLabel(category: SpiritCategory): string {
  return t(`spirit.category.${category}`);
}

export function spiritCategoryHint(category: SpiritCategory): string {
  return t(`spirit.hint.${category}`);
}

const SPIRIT_VALUE_KEYS = [
  "spirit.value.0",
  "spirit.value.1",
  "spirit.value.2",
  "spirit.value.3",
  "spirit.value.4",
] as const;

/** The 0–4 scale's wording. Out-of-range is impossible through the UI and
 *  refused by the validator, so an empty string is the honest answer. */
export function spiritValueLabel(value: number): string {
  const key = SPIRIT_VALUE_KEYS[value];
  return key === undefined ? "" : t(key);
}

// ---------- stats ----------

export function statLabel(key: StatKey): string {
  return t(`stat.${key}`);
}
